// Canonical full-replacement field set for PUT /knowledge. The backend's
// UPDATE_KNOWLEDGE_CARD writes title, parent_id, color, color_mode, content,
// tags_text, share_id, share_slug, share_description, memorize and
// review_question unconditionally, then delete-reinserts tags and link_ids —
// a payload missing any of these RESETS it. tests/putPayloadParity.test.js
// locks buildKnowledgeCardPayload's output to exactly this list (and to the
// mcp-server twin), so a new backend column fails CI until both builders
// carry it. `id` is emitted only for existing cards.
export const KNOWLEDGE_PUT_FIELDS = [
  'id',
  'title',
  'parent_id',
  'content_blocks',
  'color',
  'color_mode',
  'tags',
  'tag_ids',
  'link_ids',
  'share_id',
  'share_slug',
  'share_description',
  'private',
  'memorize',
  'review_question'
]

function normalizeId(value) {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'string' ? Number.parseInt(value, 10) : value
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

function normalizeIdList(values) {
  if (!Array.isArray(values)) return []

  return values
    .map(value => {
      if (value && typeof value === 'object') return normalizeId(value.id)
      return normalizeId(value)
    })
    .filter(id => id !== null)
}

export function buildKnowledgeCardPayload(cardLike = {}, overrides = {}) {
  // Skip `undefined` override values so a caller passing `{ private: maybeUndef }`
  // cannot clobber the base — a spread would let `Boolean(undefined)` flip a
  // private card public (matches applyOverrides in linkApiPayload.js / payloads.ts).
  const merged = { ...cardLike }
  for (const [key, value] of Object.entries(overrides)) {
    if (value !== undefined) merged[key] = value
  }
  const normalizedTags = normalizeIdList(
    Array.isArray(merged.tagIds)
      ? merged.tagIds
      : Array.isArray(merged.tags)
        ? merged.tags
        : Array.isArray(merged.tag_ids)
          ? merged.tag_ids
          : []
  )

  const payload = {
    title: merged.title || '',
    parent_id: normalizeId(merged.parent_id !== undefined ? merged.parent_id : merged.parentId),
    content_blocks: merged.content_blocks || merged.contentBlocks || [],
    color: merged.color ?? null,
    color_mode: merged.color_mode || merged.colorMode || 'border',
    tags: normalizedTags,
    tag_ids: normalizedTags,
    link_ids: normalizeIdList(merged.link_ids || merged.linkIds || []),
    share_id: merged.share_id ?? merged.shared?.id ?? null,
    share_slug: merged.share_slug ?? merged.shared?.slug ?? '',
    share_description: merged.share_description ?? merged.shared?.description ?? '',
    private: Boolean(merged.private),
    memorize: Boolean(merged.memorize),
    review_question: merged.review_question ?? merged.reviewQuestion ?? ''
  }

  const normalizedCardId = normalizeId(merged.id)
  if (normalizedCardId !== null) payload.id = normalizedCardId

  return payload
}
